import { createRoute } from '@tanstack/react-router'
import { lazyPage } from '@/lib/lazy-page'
import { dataCollectionRoute } from './data-collection-route'
import { agentPsksGridContract, normalizeDataGridUrlState, agentsGridContract } from '@/modules/data-grid'
import { decodeSubjectAuditLogSearch } from '@/modules/audit'
import { booleanParam } from '@/lib/search-params'
import { DESCENDANT_ONLY_COLUMNS } from './features/entity-agents/entity-agents-columns'

export const EntityAgentAdd = lazyPage(() => import('./features/entity-agent-add/entity-agent-add.lazy'), 'EntityAgentAdd')

const EntityAgentAddRouteComponent = lazyPage(
  () => import('./features/entity-agent-add/entity-agent-add.lazy'),
  'EntityAgentAddRouteComponent',
)

export const entityAgentAddRoute = createRoute({
  getParentRoute: () => dataCollectionRoute,
  path: 'agents/add',
  component: EntityAgentAddRouteComponent,
  validateSearch: (search: Record<string, unknown>): { mode?: 'psk' } =>
    search.mode === 'psk' ? { mode: 'psk' } : {},
})

export const EntityAgentCollectedData = lazyPage(() => import('./features/entity-agent-collected-data/entity-agent-collected-data.lazy'), 'EntityAgentCollectedData')

export const entityAgentCollectedDataRoute = createRoute({
  getParentRoute: () => dataCollectionRoute,
  path: 'agents/$agentId/collected-data',
  component: EntityAgentCollectedData,
})

export const EntityAgentConfiguration = lazyPage(() => import('./features/entity-agent-configuration/entity-agent-configuration.lazy'), 'EntityAgentConfiguration')

export const entityAgentConfigurationRoute = createRoute({
  getParentRoute: () => dataCollectionRoute,
  path: 'assets/configuration',
  component: EntityAgentConfiguration,
})

export const EntityAgentDetail = lazyPage(() => import('./features/entity-agent-detail/entity-agent-detail.lazy'), 'EntityAgentDetail')

export const entityAgentDetailRoute = createRoute({
  getParentRoute: () => dataCollectionRoute,
  path: 'agents/$agentId',
  component: EntityAgentDetail,
  validateSearch: decodeSubjectAuditLogSearch,
})

export const EntityAgentDiagnostics = lazyPage(() => import('./features/entity-agent-diagnostics/entity-agent-diagnostics.lazy'), 'EntityAgentDiagnostics')

export const entityAgentDiagnosticsRoute = createRoute({
  getParentRoute: () => dataCollectionRoute,
  path: 'agents/$agentId/diagnostics',
  component: EntityAgentDiagnostics,
})

export const EntityAgentDiscovery = lazyPage(() => import('./features/entity-agent-discovery/entity-agent-discovery.lazy'), 'EntityAgentDiscovery')

export const entityAgentDiscoveryRoute = createRoute({
  getParentRoute: () => dataCollectionRoute,
  path: 'agents/$agentId/discovery',
  component: EntityAgentDiscovery,
  validateSearch: (search: Record<string, unknown>): { request?: string } =>
    typeof search.request === 'string' ? { request: search.request } : {},
})

export const EntityAgentNetworkDiscovery = lazyPage(() => import('./features/entity-agent-network-discovery/entity-agent-network-discovery.lazy'), 'EntityAgentNetworkDiscovery')

export const entityAgentNetworkDiscoveryRoute = createRoute({
  getParentRoute: () => dataCollectionRoute,
  path: 'assets/configuration/network-discovery',
  component: EntityAgentNetworkDiscovery,
})

export const EntityAgentOperationDetail = lazyPage(() => import('./features/entity-agent-operation-detail/entity-agent-operation-detail.lazy'), 'EntityAgentOperationDetail')

export const entityAgentOperationDetailRoute = createRoute({
  getParentRoute: () => dataCollectionRoute,
  path: 'agents/$agentId/operations/$operationId',
  component: EntityAgentOperationDetail,
})

export type EntityAgentPsksSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
}

export function decodeEntityAgentPsksSearch(
  search: Record<string, unknown>,
): EntityAgentPsksSearch {
  return normalizeDataGridUrlState(search, agentPsksGridContract)
}

export const EntityAgentPsks = lazyPage(() => import('./features/entity-agent-psks/entity-agent-psks.lazy'), 'EntityAgentPsks')

export const entityAgentPsksRoute = createRoute({
  getParentRoute: () => dataCollectionRoute,
  path: 'agents/psks',
  component: EntityAgentPsks,
  validateSearch: decodeEntityAgentPsksSearch,
})

export const EntityAgentSettings = lazyPage(() => import('./features/entity-agent-settings/entity-agent-settings.lazy'), 'EntityAgentSettings')

export const entityAgentSettingsRoute = createRoute({
  getParentRoute: () => dataCollectionRoute,
  path: 'agents/$agentId/settings',
  component: EntityAgentSettings,
})

export type EntityAgentsSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
  includeDescendants?: boolean
}

const descendantOnlyColumns: readonly string[] = DESCENDANT_ONLY_COLUMNS

const columnsWithoutChildren = Object.fromEntries(Object.entries(agentsGridContract.columns)
  .filter(([column]) => !descendantOnlyColumns.includes(column)))

export function decodeEntityAgentsSearch(
  search: Record<string, unknown>,
): EntityAgentsSearch {
  const criteriaColumns = booleanParam(search.includeDescendants) === false
    ? columnsWithoutChildren
    : agentsGridContract.columns
  return {
    ...normalizeDataGridUrlState(search, { columns: criteriaColumns, parameters: agentsGridContract.parameters }),
    columns: normalizeDataGridUrlState(search, agentsGridContract).columns,
  }
}

export const EntityAgents = lazyPage(() => import('./features/entity-agents/entity-agents.lazy'), 'EntityAgents')

export const entityAgentsRoute = createRoute({
  getParentRoute: () => dataCollectionRoute,
  path: 'agents',
  component: EntityAgents,
  validateSearch: decodeEntityAgentsSearch,
})
